import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";

import Loader from "./Components/Common/Loader.js";

import RequesterDashboard from "./Components/Requester/RequesterDashboard";
import DriverDashboard from "./Components/Driver/DriverDashboard";
import AvailableRides from "./Components/Requester/AvailableRides";
import SpecialTaskRide from "./Components/Requester/SpecialTaskRide";
import MaintenanceHead from "./Components/Maintenance/MaintenanceHead";
import Requests from "./Components/Requester/Requests";
import ReportsPage from "./Components/Reports/ReportsPage";
import DriverInfo from "./Components/Driver/DriverInfo";
import SignUp from "./Components/Auth/SignUp";
import SignIn from "./Components/Auth/SignIn";
import { LogoutProvider } from "./Components/Common/LogoutProvider";
import ProtectedRoute from "./Components/ProtectedRoutes/ProtectedRoute";
import RideReportsPage from "./Components/Reports/RideReports";
import ForgotPassword from "./Components/Auth/ForgotPassword.js";
import ResetPassword from "./Components/Auth/ResetPassword.js";
import HelpButton from "./Components/Common/HelpButton.js";
// import UserCredentialsPage from "./Components/Reports/UserCredentials";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const App = () => {
  const [user, setUser] = useState(null);
  const [driver, setDriver] = useState(null);

  // 2. Run when user is set/changed
  useEffect(() => {
    if (user && user.role === "driver") {
      fetchDriverData(user.token_no, user.jwt_token);
    }
  }, [user]); // runs once after login sets user

  useEffect(() => {
    const storedUser = localStorage.getItem("user");

    if (!storedUser) {
      console.log("No stored user found, skipping fetch");
      return;
    }

    try {
      const parsedUser = JSON.parse(storedUser);

      // only set user if it has required fields
      if (parsedUser?.token_no && parsedUser?.jwt_token) {
        setUser(parsedUser);

        if (parsedUser.role === "driver") {
          fetchDriverData(parsedUser.token_no, parsedUser.jwt_token);
        }
      } else {
        console.log("Invalid stored user, clearing...");
        localStorage.removeItem("user");
      }
    } catch (err) {
      console.error("Failed to parse stored user:", err);
      localStorage.removeItem("user");
    }
  }, []);
  // ✅ empty dependency array, runs only once on mount

  const fetchDriverData = async (driverId, jwt_token) => {
    if (!jwt_token) {
      console.error("❌ No JWT token found, cannot fetch driver data");
      return;
    }

    try {
      const response = await fetch(
        `${BASE_URL}/api/drivers/getSpecificDriver/${driverId}`,
        {
          headers: { Authorization: `Bearer ${jwt_token}` },
        }
      );

      // console.log("driverData res = ", response);
      if (!response.ok) {
        throw new Error("Failed to fetch driver data");
      }
      const data = await response.json();
      setDriver(data.driver);
      // console.log("✅ Driver data fetched:", data.driver);
    } catch (error) {
      console.error("❌ Error fetching driver data:", error);
    }
  };

  const renderDashboard = () => {
    // console.log("userData = ", user);
    if (!user) return <Navigate to="/signin" />;

    switch (user.role?.toLowerCase()) {
      case "requester":
      case "admin":
        return <Navigate to="/requester" />;
      case "driver":
        return <Navigate to="/driver" />;
      case "maintenancehead":
        return <Navigate to="/maintenance-head" />;
      default:
        return <Navigate to="/signup" />;
    }
  };

  return (
    <Router>
      <LogoutProvider>
        <Routes>
          <Route path="/" element={renderDashboard()} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/signin" element={<SignIn setUser={setUser} />} />

          {/* Forgot/Reset Password routes */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* Protected Routes */}
          <Route
            path="/requester"
            element={
              <ProtectedRoute user={user} allowedRoles={["requester", "admin"]}>
                <RequesterDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/driver"
            element={
              <ProtectedRoute user={user} allowedRoles={["driver"]}>
                {driver ? <DriverDashboard driver={driver} /> : <Loader />}
              </ProtectedRoute>
            }
          />

          <Route
            path="/maintenance-head"
            element={
              <ProtectedRoute
                user={user}
                allowedRoles={["admin", "maintenanceHead"]}
              >
                <MaintenanceHead />
              </ProtectedRoute>
            }
          />

          <Route
            path="/available-rides"
            element={
              <ProtectedRoute user={user} allowedRoles={["requester", "admin"]}>
                <AvailableRides />
              </ProtectedRoute>
            }
          />

          <Route
            path="/special-task-ride"
            element={
              <ProtectedRoute user={user} allowedRoles={["requester", "admin"]}>
                <SpecialTaskRide />
              </ProtectedRoute>
            }
          />

          <Route
            path="/requests"
            element={
              <ProtectedRoute
                user={user}
                allowedRoles={["admin", "maintenanceHead"]}
              >
                <Requests />
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports"
            element={
              <ProtectedRoute
                user={user}
                allowedRoles={["admin", "maintenanceHead"]}
              >
                <ReportsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/driver-info"
            element={
              <ProtectedRoute
                user={user}
                allowedRoles={["admin", "maintenanceHead"]}
              >
                <DriverInfo />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ride-reports"
            element={
              <ProtectedRoute
                user={user}
                allowedRoles={["admin", "maintenanceHead"]}
              >
                <RideReportsPage />
              </ProtectedRoute>
            }
          />

          {/* <Route
            path="/user-credentials"
            element={
              <ProtectedRoute
                user={user}
                allowedRoles={["admin", "maintenance"]}
              >
                <UserCredentialsPage />
              </ProtectedRoute>
            }
          /> */}

          <Route
            path="*"
            element={
              <h2
                style={{ textAlign: "center", color: "red", marginTop: "20px" }}
              >
                404 - Page Not Found
              </h2>
            }
          />
        </Routes>
        <HelpButton />
      </LogoutProvider>
    </Router>
  );
};

export default App;
